'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { Label } from '@/ui/label'
import { Skeleton } from '@/ui/skeleton'
import { getMyProfile, registerHandle, updateProfile } from './actions'
import { CheckIcon, LockIcon } from '@phosphor-icons/react'

const HANDLE_REGEX = /^[a-z0-9_]{3,20}$/
const RESERVED_HANDLES = [
  'admin',
  'app',
  'api',
  'auth',
  'connect',
  's',
  'settings',
  'help',
  'support',
  'u',
]

export function ProfileForm() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [handle, setHandle] = useState('')
  const [hasHandle, setHasHandle] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [bio, setBio] = useState('')
  const [initialDisplayName, setInitialDisplayName] = useState('')
  const [initialBio, setInitialBio] = useState('')
  const [handleInput, setHandleInput] = useState('')
  const [handleError, setHandleError] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [handleSuccess, setHandleSuccess] = useState(false)
  const [profileSuccess, setProfileSuccess] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const profile = await getMyProfile()
        if (profile) {
          if (profile.handle) {
            setHandle(profile.handle)
            setHasHandle(true)
          }
          setDisplayName(profile.displayName || '')
          setBio(profile.bio || '')
          setInitialDisplayName(profile.displayName || '')
          setInitialBio(profile.bio || '')
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const validateHandle = (value: string): string | null => {
    const normalized = value.toLowerCase().trim()
    if (!normalized) return null
    if (!HANDLE_REGEX.test(normalized)) {
      return 'Must be 3-20 characters: lowercase letters, numbers, underscores'
    }
    if (RESERVED_HANDLES.includes(normalized)) {
      return 'This handle is reserved'
    }
    return null
  }

  const handleClaimHandle = async () => {
    const error = validateHandle(handleInput)
    if (error) {
      setHandleError(error)
      return
    }

    setSaving(true)
    setHandleError(null)
    try {
      const result = await registerHandle(handleInput.toLowerCase().trim())
      if (result.success) {
        setHandle(handleInput.toLowerCase().trim())
        setHasHandle(true)
        setHandleSuccess(true)
      } else {
        setHandleError(result.error || 'Failed to register handle')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    setProfileError(null)
    setProfileSuccess(false)
    try {
      const result = await updateProfile({
        displayName: displayName.trim(),
        bio: bio.trim(),
      })
      if (result.success) {
        setInitialDisplayName(displayName.trim())
        setInitialBio(bio.trim())
        setProfileSuccess(true)
        setTimeout(() => setProfileSuccess(false), 2000)
      } else {
        setProfileError(result.error || 'Failed to update profile')
      }
    } finally {
      setSaving(false)
    }
  }

  const hasProfileChanges =
    displayName.trim() !== initialDisplayName || bio.trim() !== initialBio

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Handle Section */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Handle</Label>
        {hasHandle ? (
          <div className="flex items-center gap-2">
            <div className="bg-muted flex h-10 items-center rounded-md border px-3 text-sm">
              <span className="text-muted-foreground mr-0.5">@</span>
              {handle}
            </div>
            <span className="text-muted-foreground flex items-center gap-1 text-xs">
              <LockIcon className="h-3 w-3" />
              Permanent
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm">
                  @
                </span>
                <Input
                  value={handleInput}
                  onChange={(e) => {
                    setHandleInput(e.target.value.toLowerCase())
                    setHandleError(null)
                  }}
                  onBlur={() => {
                    if (handleInput) {
                      const error = validateHandle(handleInput)
                      if (error) setHandleError(error)
                    }
                  }}
                  placeholder="your_handle"
                  className="pl-7"
                  disabled={saving}
                />
              </div>
              <Button
                onClick={handleClaimHandle}
                disabled={saving || !handleInput.trim()}
                size="default"
              >
                {saving ? 'Claiming...' : 'Claim handle'}
              </Button>
            </div>
            {handleError && (
              <p className="text-destructive text-sm">{handleError}</p>
            )}
            {handleSuccess && (
              <p className="flex items-center gap-1 text-sm text-green-500">
                <CheckIcon className="h-3.5 w-3.5" />
                Handle claimed
              </p>
            )}
          </div>
        )}
      </div>

      {/* Display Name */}
      <div className="space-y-2">
        <Label htmlFor="profile-display-name">Display name</Label>
        <Input
          id="profile-display-name"
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value)
            setProfileError(null)
          }}
          placeholder="Your name"
          maxLength={50}
          disabled={saving}
        />
      </div>

      {/* Bio */}
      <div className="space-y-2">
        <Label htmlFor="profile-bio">Bio</Label>
        <Textarea
          id="profile-bio"
          value={bio}
          onChange={(e) => {
            setBio(e.target.value)
            setProfileError(null)
          }}
          placeholder="A short bio..."
          maxLength={300}
          rows={3}
          className="resize-none"
          disabled={saving}
        />
        <p className="text-muted-foreground text-xs">{bio.length}/300</p>
      </div>

      {profileError && (
        <p className="text-destructive text-sm">{profileError}</p>
      )}

      <div className="flex items-center gap-2">
        <Button
          onClick={handleSaveProfile}
          disabled={saving || !hasProfileChanges}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        {profileSuccess && (
          <span className="flex items-center gap-1 text-sm text-green-500">
            <CheckIcon className="h-3.5 w-3.5" />
            Saved
          </span>
        )}
      </div>
    </div>
  )
}
